'use client';

import { useState } from 'react';
import { Box } from '@wishket/design-system';

import { useAuthorQuery, useAuthorSubscription } from '@/entities/author';
import { AuthorInfoCard } from '@/features/Author';
import { useDeviceCheck, useProfile, useTimedOpen } from '@/shared/hooks';
import { Skeleton } from '@/shared/Components';
import { openNavigatorShare } from '@/shared/utils';
import { PATH } from '@/shared/constants';

interface AuthorInfoProps {
  urlIdentifier: string;
}

const AuthorInfoCardSkeleton = () => {
  return (
    <Box className="col-span-full row-span-3 max-w-[1128px] px-5 pb-6 desktop:row-span-2 desktop:px-0 desktop:pb-0">
      <Skeleton className="h-[206px] desktop:h-[195px]" grayScale={50} />
    </Box>
  );
};

const AuthorInfo = ({ urlIdentifier }: AuthorInfoProps) => {
  const { isDesktop } = useDeviceCheck();
  const { profile } = useProfile();
  const { data, isLoading } = useAuthorQuery({ urlIdentifier });
  const [isShareBoxVisible, setIsShareBoxVisible] = useState(false);
  const { mutate } = useAuthorSubscription();
  const [isTooltipOpen, openTooltip] = useTimedOpen();

  const isIdentifying = profile && profile.authorIdentifier === undefined;
  const isMine = profile?.authorIdentifier === urlIdentifier;

  const toggleShareBoxVisible = () => {
    if (isDesktop) {
      setIsShareBoxVisible((prev) => !prev);
    } else {
      openNavigatorShare({ url: shareUrl, title: shareTitle });
    }
  };

  const handleSubscribeAuthor = () => {
    if (!data) return;

    mutate(data.author.id, { onSuccess: () => openTooltip() });
  };

  if (isLoading || isIdentifying) return <AuthorInfoCardSkeleton />;

  if (!data) return null;

  const shareTitle = `[요즘IT] ${data.author.name}의 작가 페이지`;
  const shareUrl = `${process.env.NEXT_PUBLIC_APP_BASE_URL}${PATH.HOME}/@${data.author.urlIdentifier}`;

  return (
    <AuthorInfoCard
      name={data.author.name}
      urlIdentifier={data.author.urlIdentifier}
      imageUrl={data.author.imageUrl}
      isCompany={data.author.isCompany}
      count={data.author.count}
      isSubscribed={data.author.isSubscribed}
      isTooltipOpen={isTooltipOpen}
      tagLine={data.author.tagLine}
      onSubscription={handleSubscribeAuthor}
      isShareBoxVisible={isShareBoxVisible}
      toggleShareBoxVisible={toggleShareBoxVisible}
      isMine={isMine}
      shareTitle={shareTitle}
      shareUrl={shareUrl}
    />
  );
};

export default AuthorInfo;
